import "./qual-view.css";
import { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { FaPrint, FaShareAlt } from "react-icons/fa";
import axios from "axios";
import {
  getSearchApiUrlWithSize,
  API_CATEGORY_LIST_URL,
  API_ROUNDS_LIST_URL,
} from "./utils/apiUrlList";
import { TRACK_EVENT_SELECTION_KEY } from "./TrackEventSelector";
import {
  buildQualRows,
  dedupeRuns,
  qualifyingRoundNames,
  formatOverUnderSeconds,
  buildQualShareText,
  buildQualShareFileName,
} from "./utils/qualSheet";
import { captureAndShare } from "./utils/shareExport";

import { api } from "./utils/apiClientWithCsrf";

// The two NHRA index categories the qualifying sheet supports. These match the
// `category` values in src/data/nhraIndexes.json exactly.
const TABS = ["Stock", "Super Stock"];

// Page size for pulling an event's runs. The backend defaults to 20; we request
// large pages so a typical event comes back in one or two round-trips.
const PAGE_SIZE = 200;
// Safety ceiling so an unexpected data shape can't loop forever.
const MAX_PAGES = 50;

const readSelection = () => {
  try {
    const raw = localStorage.getItem(TRACK_EVENT_SELECTION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const QualView = () => {
  const navigate = useNavigate();
  const [selection] = useState(readSelection);

  const [rawRuns, setRawRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [tab, setTab] = useState("Stock");
  const [sharing, setSharing] = useState(false);

  // The exportable region (header block + list) captured for share/print.
  const exportRef = useRef(null);

  const eventId = selection?.eventId;
  const trackId = selection?.trackId;
  const trackName = selection?.trackName;
  const eventName = selection?.eventName;

  // Date this sheet was generated — shown in the export header and used in the
  // share/download filename.
  const generated = useMemo(() => {
    const d = new Date();
    return {
      label: d.toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      }),
      iso: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
        d.getDate(),
      ).padStart(2, "0")}`,
    };
  }, []);

  // Fetch only the qualifying-round runs for the selected event/track, then
  // derive both tabs from that set in memory (switching tabs never refetches).
  //
  // The search API can't filter by a round *prefix*, only an exact round, so we
  // first discover which rounds exist (via the categories → rounds endpoints),
  // keep the ones starting with Q/q, and query just those. This avoids pulling
  // the whole event through /event/search (which runs a per-row opponent-driver
  // lookup) only to discard most of it.
  useEffect(() => {
    if (!eventId) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    // All pages of qualifying runs for a single exact round name.
    const fetchRoundRuns = async (round) => {
      const rows = [];
      for (let page = 0; page < MAX_PAGES; page++) {
        const response = await api.post(
          getSearchApiUrlWithSize(page, PAGE_SIZE),
          { eventId, trackId, round },
          { withCredentials: true },
        );
        const data = response.data || [];
        rows.push(...data);
        if (data.length < PAGE_SIZE) break;
      }
      return rows;
    };

    const fetchQualifyingRuns = async () => {
      setLoading(true);
      setError(false);
      try {
        // 1. List categories, then the rounds under each (rounds endpoint
        //    requires a category), and keep the distinct Q rounds.
        const catResponse = await api.get(API_CATEGORY_LIST_URL, {
          params: { eventId },
          withCredentials: true,
        });
        const categories = catResponse.data || [];

        const roundLists = await Promise.all(
          categories.map((category) =>
            api
              .get(API_ROUNDS_LIST_URL, {
                params: { category, eventId },
                withCredentials: true,
              })
              .then((r) => r.data || [])
              .catch(() => []),
          ),
        );

        const qualRounds = qualifyingRoundNames(roundLists.flat());

        // 2. No qualifying rounds → nothing to show (don't fall back to a full
        //    event fetch, which is the slow path we're avoiding).
        if (qualRounds.length === 0) {
          if (!cancelled) setRawRuns([]);
          return;
        }

        // 3. Fetch each qualifying round in parallel and merge.
        const perRound = await Promise.all(qualRounds.map(fetchRoundRuns));
        if (!cancelled) setRawRuns(dedupeRuns(perRound.flat()));
      } catch (err) {
        console.error("Error fetching qualifying runs:", err);
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchQualifyingRuns();
    return () => {
      cancelled = true;
    };
  }, [eventId, trackId]);

  // Build one row per driver for the active tab (see buildQualRows). Both tabs
  // derive from the same in-memory run set, so switching never refetches.
  const rows = useMemo(() => buildQualRows(rawRuns, tab), [rawRuns, tab]);

  // Print/share only make sense once there's something to export.
  const canExport = !loading && !error && rows.length > 0;

  const handlePrint = () => {
    try {
      window?.print?.();
    } catch (e) {
      console.error("Print failed", e);
    }
  };

  const handleShare = async () => {
    if (sharing || !canExport) return;
    setSharing(true);
    try {
      await captureAndShare(exportRef.current, {
        fileName: buildQualShareFileName({ trackName, tab, dateIso: generated.iso }),
        title: "Unofficial Qualifying Sheet",
        text: buildQualShareText(rows, { trackName, eventName, tab }),
      });
    } catch (e) {
      console.error("Share failed", e);
      alert("Sorry, sharing the qualifying sheet failed. Please try again.");
    } finally {
      setSharing(false);
    }
  };

  // No track/event selected — prompt the user to pick one.
  if (!eventId) {
    return (
      <div className="qual-container">
        <div className="qual-empty">
          <p>Select a track and event to view the qualifying sheet.</p>
          <button
            type="button"
            className="qual-select-btn"
            onClick={() => navigate("/editTrackEvent", { state: { from: "/qualView" } })}
          >
            Select track / event
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="qual-container">
      <div className="qual-header">
        <span className="qual-title">Unofficial Qualifying Sheet</span>
        <div className="qual-actions">
          <button
            type="button"
            className="qual-action-btn"
            onClick={handlePrint}
            disabled={!canExport}
            aria-label="Print qualifying sheet"
            title="Print"
          >
            <FaPrint />
          </button>
          <button
            type="button"
            className="qual-action-btn"
            onClick={handleShare}
            disabled={!canExport || sharing}
            aria-label="Share qualifying sheet"
            title="Share"
          >
            <FaShareAlt />
          </button>
        </div>
      </div>

      <div className="qual-notice" role="status">
        <span className="qual-notice-title">Loading may be slow</span>
        <span className="qual-notice-text">
          This page can take a little longer to load right now — we're building
          a faster version. Thanks for your patience.
        </span>
      </div>

      <div className="qual-tabs" role="tablist">
        {TABS.map((t) => (
          <button
            key={t}
            type="button"
            role="tab"
            aria-selected={tab === t}
            className={`qual-tab${tab === t ? " qual-tab--active" : ""}`}
            onClick={() => setTab(t)}
          >
            {t}
          </button>
        ))}
      </div>

      <div className="qual-export" ref={exportRef}>
        {/* Context header — hidden on screen, shown in the shared image and in
            print (track/event are otherwise not displayed on this page). */}
        <div className="qual-export-header">
          <div className="qual-export-title">Unofficial Qualifying Sheet</div>
          {trackName && <div className="qual-export-track">{trackName}</div>}
          {eventName && <div className="qual-export-event">{eventName}</div>}
          <div className="qual-export-meta">
            {tab} · {generated.label}
          </div>
        </div>

        {loading ? (
          <div className="loader"></div>
        ) : error ? (
          <div className="qual-message">
            Sorry, the qualifying data couldn't be loaded. Please try again.
          </div>
        ) : rows.length === 0 ? (
          <div className="qual-message">No qualifying runs for {tab}.</div>
        ) : (
          <div className="qual-list">
            {rows.map((row, index) => (
              <div className="qual-card" key={`${row.driver}-${row.carNo}-${index}`}>
                <div className="qual-card-top">
                  <span className="qual-rank">{index + 1}</span>
                  <span className="qual-driver">{row.driver || "-"}</span>
                  <span className="qual-ou">
                    <span className="qual-ou-label">Ov/Un</span>
                    <span className="qual-ou-value">
                      {formatOverUnderSeconds(row.overUnderMs)}
                    </span>
                  </span>
                </div>
                <div className="qual-card-stats">
                  <div className="qual-stat">
                    <span className="qual-stat-label">Car</span>
                    <span className="qual-stat-value">{row.carNo || "-"}</span>
                  </div>
                  <div className="qual-stat">
                    <span className="qual-stat-label">Class</span>
                    <span className="qual-stat-value">{row.carClass}</span>
                  </div>
                  <div className="qual-stat">
                    <span className="qual-stat-label">Category</span>
                    <span className="qual-stat-value">{row.category}</span>
                  </div>
                  <div className="qual-stat">
                    <span className="qual-stat-label">Index</span>
                    <span className="qual-stat-value">
                      {row.index != null ? row.index : "-"}
                    </span>
                  </div>
                  <div className="qual-stat">
                    <span className="qual-stat-label">Final ET</span>
                    <span className="qual-stat-value">
                      {row.finalEt != null ? row.finalEt.toFixed(3) : "-"}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default QualView;
